import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, Phone, Mail, Calendar, Eye, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NavBar } from '@/components/NavBar';
import { PropertyHero } from '@/components/PropertyHero';
import { PropertyGallery } from '@/components/PropertyGallery';
import { FloorPlanViewer } from '@/components/FloorPlanViewer';
import { PropertyCTA } from '@/components/PropertyCTA';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { Footer } from '@/components/Footer';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { type Property, getPropertyById, getNextProperty } from '@/data/properties';
import { trackEvent } from '@/lib/analytics';

export const PropertyDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | undefined>(undefined);
  const [activeTab, setActiveTab] = useState('overview');
  const [isFavorite, setIsFavorite] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [nextProperty, setNextProperty] = useState<Property | null>(null);

  // WhatsApp number - in production this would come from env variable
  const whatsappNumber = '13451234567'; // Replace with actual number
  const whatsappMessage = encodeURIComponent(
    `Hi! I'm interested in learning more about ${property?.name} at The Pearl at Grand Harbour.`
  );

  // Update property and nextProperty when id changes
  useEffect(() => {
    if (id) {
      const currentProperty = getPropertyById(id);
      if (!currentProperty) {
        navigate('/404');
        return;
      }
      setProperty(currentProperty);
      const next = getNextProperty(id);
      setNextProperty(next);
    }
  }, [id, navigate]);

  // Track page view when property is loaded
  useEffect(() => {
    if (property) {
      trackEvent('property_page_view', { 
        property: property.name,
        property_id: property.id 
      });

      // Set page title
      document.title = `${property.name} ${property.type} - The Pearl at Grand Harbour`;
    }
  }, [property]);

  const handleBackClick = () => {
    navigate('/#residences');
    trackEvent('property_page_back', { property: property?.name });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${property?.name} ${property?.type} - The Pearl`,
        text: `Check out this luxury ${property?.type.toLowerCase()} at The Pearl at Grand Harbour`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Show toast notification
    }
    trackEvent('property_page_share', { property: property?.name });
  };

  const handleFavorite = () => {
    setIsFavorite(!isFavorite);
    trackEvent('property_page_favorite', { 
      property: property?.name, 
      action: isFavorite ? 'remove' : 'add' 
    });
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    trackEvent('property_page_tab_change', { 
      property: property?.name, 
      tab: value 
    });
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
    trackEvent('whatsapp_click', { 
      property: property?.name,
      location: 'property_details'
    });
  };

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-teal mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-foreground mb-4">Loading Property...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <NavBar 
        onRegisterClick={() => setLeadFormOpen(true)} 
        onConsultClick={() => setMeetingOpen(true)}
      />

      {/* Hero Section */}
      <PropertyHero property={property} />

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Property Actions */}
          <div className="flex items-center justify-between mb-8">
            <Button
              variant="ghost"
              onClick={handleBackClick}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Residences
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShare}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFavorite}
                className={`gap-2 ${isFavorite ? 'text-red-500' : ''}`}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                Save
              </Button>
            </div>
          </div>

          {/* Property Info Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  {property.name} {property.type}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                  <span>{property.sqft} sq ft</span>
                  <span>•</span>
                  <span>{property.beds} bed{property.beds > 1 ? 's' : ''}</span>
                  <span>•</span>
                  <span>{property.baths} bath{property.baths > 1 ? 's' : ''}</span>
                  {property.priceRange && (
                    <>
                      <span>•</span>
                      <Badge variant="outline" className="text-primary border-primary">
                        {property.priceRange}
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2">
                  <Phone className="w-4 h-4" />
                  Call Now
                </Button>
                <Button className="gap-2" onClick={() => setLeadFormOpen(true)}>
                  <Mail className="w-4 h-4" />
                  Register Interest
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-8">
            <TabsList className="flex flex-wrap w-full gap-2 sm:gap-3 justify-center lg:grid lg:grid-cols-3 mb-10 rounded-xl bg-muted p-1">
              <TabsTrigger value="overview" className="text-sm flex-none min-w-0 whitespace-normal">Overview</TabsTrigger>
              <TabsTrigger value="gallery" className="text-sm flex-none min-w-0 whitespace-normal">Gallery</TabsTrigger>
              <TabsTrigger value="floorplan" className="text-sm flex-none min-w-0 whitespace-normal">Floor Plan</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              {/* Description */}
              <Card>
                <CardContent className="p-8">
                  <h2 className="font-display text-2xl font-semibold mb-4">About This Property</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6 text-lg">
                    {property.description}
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Key Features */}
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Key Features</h3>
                      <ul className="space-y-3">
                        {property.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Property Details */}
                    <div>
                      <h3 className="font-semibold text-lg mb-4">Property Details</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">View Direction:</span>
                          <span className="font-medium">{property.viewDirection}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Finishes Level:</span>
                          <span className="font-medium">{property.finishesLevel}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Parking:</span>
                          <span className="font-medium">{property.parkingSpaces} space{property.parkingSpaces > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Pet Friendly:</span>
                          <span className="font-medium">{property.petFriendly ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Availability:</span>
                          <span className="font-medium">{property.availabilityDate}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="mt-8">
                    <h3 className="font-semibold text-lg mb-4">Highlights</h3>
                    <div className="flex flex-wrap gap-3">
                      {property.highlights.map((highlight, index) => (
                        <Badge key={index} variant="secondary" className="text-sm">
                          {highlight}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="gallery">
              <PropertyGallery images={property.images} propertyName={property.name} />
            </TabsContent>

            <TabsContent value="floorplan">
              <FloorPlanViewer 
                floorPlanImages={[property.floorPlanImage]}
                propertyName={property.name}
                className="h-[600px]"
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Interior Design Customising Section */}
      <section className="py-16 bg-background border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content Side */}
              <div className="space-y-6">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground">
                  Interior Design Customising
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Personalize your residence with our bespoke interior design services. 
                  Work with our design team to create a space that reflects your unique style and preferences.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Custom finishes and materials</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Personalized color schemes</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                    <span>Furniture and fixture selection</span>
                  </li>
                </ul>
                <Button
                  size="lg"
                  className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                  onClick={() => {
                    trackEvent('interior_design_cta_click', { 
                      property: property?.name,
                      location: 'property_details'
                    });
                    // External URL - leave blank for now, will be configured later
                    const interiorDesignUrl = ''; // TODO: Add external URL
                    if (interiorDesignUrl) {
                      window.open(interiorDesignUrl, '_blank');
                    }
                  }}
                >
                  Explore Interior Design Options
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              
              {/* Visual Side */}
              <div className="relative overflow-hidden rounded-2xl shadow-luxury">
                <img
                  src="/img/residence-interior.jpg"
                  alt="Interior design customization"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Property Navigation */}
      {nextProperty && (
        <section className="py-16 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-4">
                  Explore Next Property
                </h2>
                <p className="text-lg text-muted-foreground">
                  Continue your journey through our luxury collection
                </p>
              </div>

              <div className="relative group">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  {/* Next Property Image */}
                  <div className="relative overflow-hidden rounded-2xl shadow-luxury group-hover:shadow-2xl transition-all duration-500">
                    <img
                      src={nextProperty.images[0]}
                      alt={`${nextProperty.name} interior view`}
                      className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4">
                      <Badge variant="secondary" className="bg-brand-teal/20 text-brand-teal border-brand-teal">
                        {nextProperty.type}
                      </Badge>
                    </div>
                  </div>

                  {/* Next Property Info */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-display text-3xl lg:text-4xl font-bold text-foreground mb-4 group-hover:text-brand-teal transition-colors duration-300">
                        {nextProperty.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-4">
                        <span>{nextProperty.sqft} sq ft</span>
                        <span>•</span>
                        <span>{nextProperty.beds} bed{nextProperty.beds > 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span>{nextProperty.baths} bath{nextProperty.baths > 1 ? 's' : ''}</span>
                        {nextProperty.priceRange && (
                          <>
                            <span>•</span>
                            <Badge variant="outline" className="text-primary border-primary">
                              {nextProperty.priceRange}
                            </Badge>
                          </>
                        )}
                      </div>
                      <p className="text-muted-foreground leading-relaxed text-lg">
                        {nextProperty.description}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      {nextProperty.highlights.slice(0, 3).map((highlight, index) => (
                        <Badge key={index} variant="secondary" className="text-sm">
                          {highlight}
                        </Badge>
                      ))}
                    </div>

                    <Button
                      size="lg"
                      className="bg-champagne-gold hover:bg-champagne-gold/90 text-primary hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                      onClick={() => {
                        trackEvent('next_property_click', { 
                          from_property: property?.name,
                          to_property: nextProperty.name 
                        });
                        navigate(`/property/${nextProperty.slug}`);
                      }}
                    >
                      <Eye className="w-5 h-5 mr-2" />
                      View {nextProperty.name} Details
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Sticky CTA */}
      <PropertyCTA property={property} />

      {/* Footer */}
      <Footer />

      {/* Persistent Conversion UI */}
      <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
      <WhatsAppButton onClick={handleWhatsAppClick} />
      <ScrollToTopButton />

      {/* Modals */}
      <LeadForm open={leadFormOpen} onOpenChange={setLeadFormOpen} />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
      />
    </div>
  );
};
